import { useTranslation } from "react-i18next";

import { Button } from "@wellcheck/components/ui/button";

import type { AnswersDraft, SurveyCampaign } from "@/features/respond/data/types";
import type { ReactNode } from "react";

interface ReviewScreenProps {
  campaign: SurveyCampaign;
  answers: AnswersDraft;
  progress: ReactNode;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  // 409 (二重提出) 以外の送信失敗メッセージ。undefined ならエラー表示なし。
  submitError?: string;
}

/**
 * ReviewScreen — 送信前の最終確認。
 *
 * section ごとに回答を並べて目視確認できるようにする。送信ボタンを押すと
 * 親 (SurveyForm) が POST → 成功時に clearDraft + 画面遷移を行う。
 * 送信中は二重送信を防ぐため送信ボタンを disabled にする。
 */
export function ReviewScreen({
  campaign,
  answers,
  progress,
  onBack,
  onSubmit,
  isSubmitting,
  submitError,
}: ReviewScreenProps) {
  const { t } = useTranslation("respond");
  return (
    <div className="space-y-10">
      {progress}

      <section className="space-y-6">
        <header className="space-y-1">
          <h2 className="text-base font-semibold">{t("review.title")}</h2>
          <p className="text-sm text-muted-foreground">{t("review.description")}</p>
        </header>

        <div className="space-y-8">
          {campaign.sections.map((section) => (
            <div key={section.id} className="space-y-3">
              <h3 className="text-sm font-semibold">{section.title}</h3>
              <dl className="divide-y divide-border border-t border-border">
                {section.questions.map((q) => {
                  const v = answers[q.id];
                  const label =
                    q.type === "four-scale" && section.scale && typeof v === "number"
                      ? `${v}. ${section.scale.find((s) => s.value === v)?.label ?? ""}`
                      : q.type === "single-choice" && typeof v === "string"
                        ? (q.choices.find((c) => c.value === v)?.label ?? v)
                        : typeof v === "string"
                          ? v
                          : t("review.noAnswer");
                  return (
                    <div key={q.id} className="grid grid-cols-[3rem_1fr_auto] gap-3 py-2 text-sm">
                      <dt className="text-muted-foreground tabular-nums">{q.id}</dt>
                      <dd className="text-foreground">{q.label}</dd>
                      <dd className="text-foreground tabular-nums">{label}</dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          ))}
        </div>
      </section>

      {submitError && (
        <p role="alert" className="text-sm text-destructive">
          {submitError}
        </p>
      )}

      <div className="flex items-center justify-end gap-6 border-t border-border pt-6">
        <Button
          variant="link"
          onClick={onBack}
          disabled={isSubmitting}
          className="px-0 text-muted-foreground"
        >
          {t("review.back")}
        </Button>
        <Button size="lg" onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting ? t("review.submitting") : t("review.submit")}
        </Button>
      </div>
    </div>
  );
}
